import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation,
  usePlatformConfig
} from "@kivunova/kivufrontendcommon";
import { formatUserRoleLabel } from "../Profile/utils/userRoleLabels";
import { PAGE_PORTAL_PROFILE } from "@kivunova/kivufrontendcommon";
import { useGetProfile } from "../../hooks/api/useProfile";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  Box,
  Avatar,
  Typography,
  Tooltip,
  Skeleton,
  CircularProgress
} from "@mui/material";
import { Logout } from "@mui/icons-material";
import AppButton from "../common/AppButton";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  AlertBannerContext
} from "@kivunova/kivufrontendcommon";
import useLogout from "../../hooks/api/useLogout";
import {
  sidebarLogoutButtonSx,
  sidebarProfileAvatarSx,
  sidebarProfileCardSx
} from "./sidebarStyles";

const ProfileCard: React.FC = () => {
  const navigate = useNavigate();
  const { setAlert } = useContext(AlertBannerContext);
  const { language, defaultLang } = useContext(KivuI18nContext);
  const profileLink = localizedPath(
    `${PAGE_PORTAL_PROFILE}`,
    language,
    defaultLang
  );
  const { profileData } = useSelector((state: RootState) => state.profile);
  const getProfileMutation = useGetProfile();
  const useLogoutMutation = useLogout();

  const { t } = useKivunovaTranslation();
  const { config } = usePlatformConfig();

  useEffect(() => {
    (async () => {
      try {
        await getProfileMutation.mutateAsync();
      } catch {
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
      }
    })();
  }, []);

  const handleNavigate = () => {
    navigate(profileLink);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(profileLink);
    }
  };

  const handleLogout = async () => {
    try {
      await useLogoutMutation.mutateAsync();
    } catch (e) {
      console.error("Failed to logout due to error", e);
    }
  };

  return (
    <Box
      component="article"
      role="article"
      aria-label="Profile Card"
      sx={sidebarProfileCardSx}
    >
      <Box
        onClick={
          getProfileMutation.status === "pending" ? undefined : handleNavigate
        }
        onKeyDown={
          getProfileMutation.status === "pending" ? undefined : handleKeyDown
        }
        tabIndex={getProfileMutation.status === "pending" ? -1 : 0}
        role="button"
        aria-label="View profile details"
        aria-busy={getProfileMutation.status === "pending"}
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flex: 1,
          minWidth: 0,
          textDecoration: "none",
          color: "inherit",
          cursor:
            getProfileMutation.status === "pending" ? "default" : "pointer",
          borderRadius: 1,
          "&:focus": {
            outline: "2px solid rgba(255,255,255,0.6)",
            outlineOffset: 2
          },
          "&:hover": {
            "& .profile-name": { opacity: 0.95 },
            "& .profile-role": { opacity: 1 }
          }
        }}
      >
        {getProfileMutation.status === "pending" ? (
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.12)"
            }}
          >
            <CircularProgress
              size={24}
              thickness={4}
              sx={{ color: "rgba(255,255,255,0.9)" }}
            />
          </Box>
        ) : (
          <Avatar
            src={profileData?.profileImage}
            alt={profileData?.firstName}
            sx={sidebarProfileAvatarSx}
          />
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {getProfileMutation.status === "pending" ? (
            <>
              <Skeleton
                variant="text"
                width={80}
                height={20}
                animation="wave"
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  mb: 0.5,
                  "&::after": {
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)"
                  }
                }}
              />
              <Skeleton
                variant="text"
                width={60}
                height={16}
                animation="wave"
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  "&::after": {
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)"
                  }
                }}
              />
            </>
          ) : (
            <>
              <Typography
                className="profile-name"
                variant="subtitle1"
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.9375rem",
                  lineHeight: 1.35
                }}
              >
                {profileData?.firstName || t("user-name", "Username")}
              </Typography>
              <Typography
                className="profile-role"
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  fontSize: "0.8125rem",
                  lineHeight: 1.4
                }}
              >
                {profileData?.role
                  ? formatUserRoleLabel(
                      profileData.role,
                      t,
                      config?.businessType
                    )
                  : t("profile-role", "Role")}
              </Typography>
            </>
          )}
        </Box>
      </Box>
      <Tooltip title={t("logout", "Logout")}>
        <AppButton
          variant="text"
          onClick={handleLogout}
          sx={sidebarLogoutButtonSx}
        >
          <Logout fontSize="small" sx={{ color: "#fff" }} />
        </AppButton>
      </Tooltip>
    </Box>
  );
};

export default ProfileCard;
